<script setup lang="ts">
    import type { Template } from '@/domain/template/template';
    import type { ApiCase } from '@/api/case';
    import { computed } from 'vue';
    import RerunTemplate from '@/views/admin/RerunTemplate.vue';
    import { useCaseAdminStore } from '@/views/admin/adminStore';
    import { capitalize } from 'lodash';
    import { DateTime } from 'luxon';

    const props = defineProps<{ template: Template; case: ApiCase }>();
    const store = useCaseAdminStore();
    type _TemplateState = 'error' | 'processing' | 'completed' | 'manual' | 'new' | 'other';

    const templateState = computed<_TemplateState>(
        () => {
            const state = props.template.state;
            const recordState = props.template.recordState;
            if (state === 'error' || recordState === 'error') {
                return 'error';
            } else if (state === 'processing' || recordState === 'processing') {
                return 'processing';
            } else if (state === 'completed' && recordState === 'completed') {
                return 'completed';
            } else if (state === 'manual') {
                return 'manual';
            } else if (state === 'new') {
                return 'new';
            } else {
                return 'other';
            }
        },
    );

    const stateIcon = computed(() => {
        const map = {
            'error': 'mdi-timer-alert',
            'processing': 'mdi-timer-sync',
            'completed': 'mdi-timer-check',
            'manual': 'mdi-account',
            'new': 'mdi-timer-star',
            'other': 'mdi-timer',
        } as Record<_TemplateState, string>;
        return map[templateState.value];
    });

    const stateIconColor = computed(() => {
        const map = {
            'new': '#eebb00',
            'error': 'error',
            'processing': '#f47920',
            'completed': 'success',
            'manual': 'primary',
            'other': 'grey',
        } as Record<_TemplateState, string>;
        return map[templateState.value];
    });

    const type = computed(() => store.templateType(props.template));

    const created = computed(() => DateTime.fromISO(props.template.created).toHTTP());
    const updated = computed(() => DateTime.fromISO(props.template.updated).toHTTP());
</script>

<template>
    <v-expansion-panel>
        <v-expansion-panel-title>
            <div class="d-flex align-center ga-2">
                <span class="text-heading-h4">
                    Template {{ template.id.template }}
                </span>
                <v-icon
                    v-tooltip="capitalize(templateState)"
                    :icon="stateIcon"
                    :color="stateIconColor"
                />
                <v-icon
                    v-if="type === 'auto'"
                    v-tooltip="'Current automated-template'"
                    icon="mdi-star"
                    :color="stateIconColor"
                />
                <v-icon
                    v-if="type === 'user'"
                    v-tooltip="'Current user template'"
                    icon="mdi-star"
                    :color="stateIconColor"
                />
                <div v-if="templateState === 'completed'">
                    <RerunTemplate :template="template" />
                </div>
            </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
            <div class="property-grid">
                <label>link:</label>
                <a :href="template.self" target="_blank"> {{ template.self }} </a>

                <label>canonical:</label>
                <a :href="template.canonical" target="_blank"> {{ template.canonical }} </a>

                <label>state:</label>
                <span> {{ template.state }} </span>

                <label>record-state:</label>
                <span> {{ template.recordState }} </span>

                <label>created:</label>
                <span> {{ created }} </span>

                <label>updated:</label>
                <span> {{ updated }} </span>

                <label>study:</label>
                <a :href="template.study"> {{ template?.study }} </a>

                <label>components:</label>
                <a :href="template.components"> {{ template?.components }} </a>

                <label>plan:</label>
                <a :href="template.plan" target="_blank"> {{ template.plan }} </a>
            </div>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>

<style scoped lang="scss">
    .property-grid {
        display: grid;
        grid-template-columns: min-content auto;
        column-gap: 1rem;
    }

    .property-grid > label {
        font-weight: bold;
    }

    a {
        color: rgb(var(--v-theme-primary));
    }
</style>
